import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useAuth } from "../../contexts/authContext";
import { useSignedInWithOptro } from "../../hooks/useSignedInWithOptro";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { switchWorkspace } from "../../lib/switchWorkspace";
import { cn } from "../../lib/utils";

interface WorkspaceSwitcherProps {
  isCollapsed: boolean;
}

export const WorkspaceSwitcher = ({ isCollapsed }: WorkspaceSwitcherProps) => {
  const { workspace, workspaces } = useAuth();

  const isOptroSession = useSignedInWithOptro();

  // Optro-authenticated sessions are locked to the workspace they signed in
  // with — the backend rejects a mismatched X-Workspace-Id — so hide the
  // switcher entirely. Native/WorkOS (sales/internal) sessions keep it.
  if (isOptroSession) {
    return null;
  }

  if (workspaces.length <= 1) {
    return null;
  }

  const switchTo = (workspaceId: string) => {
    if (!workspaceId || workspaceId === workspace?.id) {
      return;
    }
    switchWorkspace(workspaceId);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 w-full p-3 hover:bg-stone-50 border border-transparent text-sm cursor-pointer transition-colors duration-200 rounded-xs",
          )}
          aria-label="Switch workspace"
        >
          <Building2 size={20} className="flex-shrink-0" />
          <span
            className={cn(
              "flex-1 text-left truncate transition-all duration-300",
              isCollapsed ? "w-0 opacity-0 overflow-hidden" : "opacity-100",
            )}
          >
            {workspace?.name ?? "Select workspace"}
          </span>
          <ChevronsUpDown
            size={14}
            className={cn(
              "text-stone-400 flex-shrink-0 transition-all duration-300",
              isCollapsed ? "w-0 opacity-0 overflow-hidden" : "opacity-100",
            )}
          />
        </button>
      </DropdownMenuTrigger>
      {/* Capped at the space Radix measures above the trigger, with only the
          list scrolling, so a long workspace list can't run off the top of
          the window and the label stays put. */}
      <DropdownMenuContent
        align="start"
        side="top"
        className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[220px] flex-col"
      >
        <DropdownMenuLabel className="text-xs text-stone-500 font-normal">
          Switch workspace
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="min-h-0 overflow-y-auto">
          {workspaces.map((w) => {
            const isCurrent = w.id === workspace?.id;
            return (
              <DropdownMenuItem
                key={w.id}
                disabled={isCurrent}
                onSelect={() => switchTo(w.id)}
                className="flex items-center gap-2"
              >
                <span className="flex-1 truncate">{w.name}</span>
                {isCurrent ? (
                  <Check size={14} className="text-stone-500 flex-shrink-0" />
                ) : null}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
